import React, { createContext, useContext, useState, useCallback } from 'react';
import { ManagementAccount, ManagementAccountLoginResponse, ManagementAuthContextType } from '@/types';
import api from '../services/api';
import { MANAGEMENT_TOKEN_KEY, MANAGEMENT_ACCOUNT_KEY } from '../constants/authKeys';

const ManagementAuthContext = createContext<ManagementAuthContextType>({
  managementAccount: null,
  managementToken: null,
  isManagementUser: false,
  isSuperadmin: false,
  loginAsManagement: async () => ({ token: '', account_type: 'not_found' }),
  tryAutoLogin: async () => false,
  logoutManagement: () => {},
  refreshAccount: async () => {},
});

export const useManagementAuth = () => useContext(ManagementAuthContext);

export const ManagementAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // 初期化: localStorageからトークンを同期的に復元（lazy initializer）
  const [managementAccount, setManagementAccount] = useState<ManagementAccount | null>(() => {
    const saved = localStorage.getItem(MANAGEMENT_ACCOUNT_KEY);
    if (saved) {
      try { return JSON.parse(saved); } catch { return null; }
    }
    return null;
  });
  const [managementToken, setManagementToken] = useState<string | null>(
    () => localStorage.getItem(MANAGEMENT_TOKEN_KEY)
  );

  const loginAsManagement = useCallback(async (email: string, password: string): Promise<ManagementAccountLoginResponse> => {
    const response = await api.managementAuth.login({ email, password });

    if (response.account_type === 'management' && response.token && response.account) {
      setManagementToken(response.token);
      setManagementAccount(response.account);
      localStorage.setItem(MANAGEMENT_TOKEN_KEY, response.token);
      localStorage.setItem(MANAGEMENT_ACCOUNT_KEY, JSON.stringify(response.account));
    }

    return response;
  }, []);

  const tryAutoLogin = useCallback(async (email: string): Promise<boolean> => {
    try {
      const response = await api.managementAuth.loginByEmail(email);
      if (response.account_type === 'management' && response.token && response.account) {
        setManagementToken(response.token);
        setManagementAccount(response.account);
        localStorage.setItem(MANAGEMENT_TOKEN_KEY, response.token);
        localStorage.setItem(MANAGEMENT_ACCOUNT_KEY, JSON.stringify(response.account));
        return true;
      }
    } catch {
      // 自動ログイン失敗は無視
    }
    return false;
  }, []);

  const logoutManagement = useCallback(() => {
    setManagementToken(null);
    setManagementAccount(null);
    localStorage.removeItem(MANAGEMENT_TOKEN_KEY);
    localStorage.removeItem(MANAGEMENT_ACCOUNT_KEY);
  }, []);

  const refreshAccount = useCallback(async () => {
    if (!managementToken) return;
    try {
      const account = await api.managementAuth.getMe(managementToken);
      setManagementAccount(account);
      localStorage.setItem(MANAGEMENT_ACCOUNT_KEY, JSON.stringify(account));
    } catch {
      logoutManagement();
    }
  }, [managementToken, logoutManagement]);

  const value: ManagementAuthContextType = {
    managementAccount,
    managementToken,
    isManagementUser: !!managementToken && !!managementAccount,
    isSuperadmin: !!managementToken && !!managementAccount?.is_superadmin,
    loginAsManagement,
    tryAutoLogin,
    logoutManagement,
    refreshAccount,
  };

  return (
    <ManagementAuthContext.Provider value={value}>
      {children}
    </ManagementAuthContext.Provider>
  );
};

export default ManagementAuthContext;
